import { LogoType } from '@app/branding/models/branding.model';
import { UserPlan } from './user-plan';

export type VendastaAccountId = string;
export const PlatformPartnerExternalIdType = 'v';
export const ExternalIdTypes = [PlatformPartnerExternalIdType] as const;

export type ExternalIdType = typeof ExternalIdTypes[number];

// TODO: Move this into OrgModule

/** From server: IOrganizationSchema */
export interface IOrganization {
    id: string;
    name: string;
    domain: string;
    extraDomains: string[];
    admins: string[];
    logoUrl: string;
    avatar: string;
    avatar_image: number;
    apps: {
        faq: {
            items: any[];
        };
        docgen: {
            items: Array<{
                name: { type: string }; // the name of the document that the user will reference in their text
                teams: { type: string[]; index: true }; // which 'teams' can see this response
                providerId: {
                    type: string;
                    ref: 'provider';
                    index: true;
                };
                fileXref: { type: string }; // the document ID in Google Drive, MS OneDrive
                fileName: { type: string };
                questions: Array<{
                    ask: { type: string }; // optional if default is set
                    variable: { type: string }; // the variable in the DOCX to replace
                    default: { type: string };
                }>;
            }>;
        };
    };
    savings: any;
    billing?: {
        customerId: string;
        subscriptionId: string;
        datePaused: string | Date;
        /**
         * Set in Ava and indicates that it's billed outside of Stripe, i.e. Xero
         */
        externalPaymentStartDate: Date;
    };
    billingUseActiveUsers: boolean;
    billingStartDate: Date; // the 'bill date' is when after a pilot we want to charge a corp. self serve eterprise we use dateAdded
    customPricing: // keep this array sorted from least to greatest
    Array<{
        maxUsers: number; // the max amount of users in this bucket
        pricePerUser: number; // how much it costs for them
    }>;
    options: IOrgOptions;
    /** discriminan */
    orgExists: true;
    /** Vendasta fields */
    vendastaAccountId: VendastaAccountId;
    vendastaSeatsClaimedIds?: Array<string>;
    externalId?: string;
    externalIdType?: ExternalIdType;
}

export interface IOrgOptions {
    test: boolean;
    email_footer: string;
    restrictedApps: string[];
    maxUsers: number;
    freeUsers?: number; // the number of "free" users that the AppSumo purchase gave this account
    restrictedMessengers: string[]; // not used
    /**
     * @deprecated Use `branding.showOrgBranding` instead
     */
    showOrgBranding?: boolean;
    branding?: {
        showOrgBranding?: boolean;
        logo?: {
            url: string;
            type: LogoType;
        };
        cname?: string;
    };
    autoAdd: boolean;
    hideNegativeWhoIs: boolean;
    allowLeaving: boolean;
    allowAvatarChange: boolean;
    hidePersonalityTraits: boolean;
    hideAutomatedAssistant: boolean;
    hideImportCalendar: boolean;
    hideZoomFAQ: boolean;
    enableZoomFAQHelpMessage: boolean;
    intranetHelp: string;
    customerSupportHelp: string;
    welcome: string;
    welcomeEmail: string;
    welcomeEmailSubject: string;
    customFailResponse: string;
    allowedEmails: string[];
    askEmailScopeUpgrade: boolean;
    useCustomInviteEmail: boolean;
    customInviteEmail?: string; // this is not optional on server but it may be missing in payload
    /** Vendasta-specific settings */
    vAutoAddAdmin: boolean;
    vAutoClaimSeat: boolean;
}

/** returns when user does not belong to an org and we query /user/org */
export interface INonExistentOrg {
    /** discriminant */
    orgExists: false;
    canUpgradeToOrg: boolean;
}

/** Unlike IOrganization, this is a smaller model containing only fields relevant to that user's own org e.g. no org-wide options */
export interface IUserOrg {
    orgId: string;
    plan: UserPlan;
    canUpgradeToOrg: boolean;
    isOrgAdmin: boolean;
    orgCount: number;
    orgName: string;
    orgDomain: string;
    logoUrl: string;
    options: IOrgOptions;
    billing: {
        customerId?: string;
        subscriptionId?: string;
        startDate?: Date;
        customPricing: Array<{
            maxUsers: number;
            pricePerUser: number;
        }>;
        useActiveUsers?: boolean;
        externalPayment?: Date;
        datePaused?: string | Date;
    };
    orgHasRoomProviders: boolean;
    restrictedApps: string[];
    orgExists: boolean;
}

export type IUserOrNonExistentOrg = IUserOrg | INonExistentOrg;
